import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Gift, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { AnimatedIcon } from '@/shared/components/animated-icon';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { displayMgmFabUrl, MGM_FAB_DEFAULT_LABEL } from '../model/mgm-fab';
import { FabIcon } from './fab-icon';

const COPIED_MS = 2000;

/** Link do botão com "Copiar link"; a confirmação aparece no próprio botão. */
export function MgmFabLink({ url, className }: { url: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copiado.', { duration: 1600 });
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-xl border border-border bg-background/80 py-1.5 pl-3 pr-1.5',
        className,
      )}
    >
      <Link2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground/80" title={url}>
        {displayMgmFabUrl(url)}
      </span>
      <Button
        type="button"
        size="sm"
        variant={copied ? 'success' : 'primary'}
        className="shrink-0"
        onClick={() => void copy()}
      >
        {copied ? <Check /> : <Copy />}
        {copied ? 'Copiado' : 'Copiar link'}
      </Button>
    </div>
  );
}

interface MgmFabPreviewProps {
  url: string;
  label: string;
  color: string;
  iconColor: string;
  svgContent: string | null;
  iconUrl?: string;
}

/**
 * Prévia final do botão do Indique e Ganhe: como ele aparece no app e o link
 * pronto para copiar. Cada cliente abre a página com o próprio CPF, que o app
 * completa no fim do link.
 */
export function MgmFabPreview({ url, label, color, iconColor, svgContent, iconUrl }: MgmFabPreviewProps) {
  return (
    <section className="overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-card shadow-soft">
      <div className="flex items-center gap-4 p-4 sm:p-5">
        <FabIcon
          iconUrl={iconUrl}
          svgContent={svgContent}
          fallbackIcon={Gift}
          backgroundColor={color}
          iconColor={iconColor}
          className="size-14 shrink-0 rounded-full shadow-soft ring-4 ring-background sm:size-16"
          iconClassName="size-6 sm:size-7"
        />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <AnimatedIcon icon={Gift} motion="wiggle" className="size-3.5 text-primary" />
            Indique e Ganhe
          </p>
          <p className="mt-0.5 truncate text-base font-semibold">{label.trim() || MGM_FAB_DEFAULT_LABEL}</p>
          <p className="text-xs text-muted-foreground sm:text-sm">Abre a página de indicação do cliente dentro do app.</p>
        </div>
      </div>
      <div className="space-y-2 border-t border-border/60 bg-background/40 p-4 sm:p-5">
        <p className="text-xs font-medium text-muted-foreground">Link do botão</p>
        <MgmFabLink url={url} />
        <p className="text-xs text-muted-foreground">
          O app completa o link com o CPF de cada cliente. O CPF fica depois do <code>#</code> e não vai para
          nenhum servidor.
        </p>
      </div>
    </section>
  );
}
